import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, ArrowLeft, FileText, Receipt, CreditCard, FileX, 
  Filter, Briefcase, TrendingUp, AlertCircle, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Loader2, ExternalLink, Globe
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import PageHero from "../components/PageHero";
import PageScopeBanner from "../components/PageScopeBanner";
import OpenMatterRemoveButton from "../components/OpenMatterRemoveButton";
import { getUserRole, ROLES } from "../config/roleConfig";
import axios from "axios";

import { API } from '../config/api';
import { clientDisplayName } from "../utils/clientDisplayName";

// BM-H3717: every row opens the matter's own page.
const matterPath = (ref) => `/matter/${encodeURIComponent(ref)}`;

function formatCurrency(val) {
  if (!val && val !== 0) return "—";
  return `£${Number(val).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// BM-H3468: directors and admins (client care) may remove a matter from
// this list. The server enforces the same rule; this only hides the button
// from people it would refuse.
function canRemoveMatters() {
  try {
    const userObj = JSON.parse(localStorage.getItem("user") || "null");
    const email = (userObj?.user_email || userObj?.email || userObj?.ms_email
      || sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "").toLowerCase().trim();
    const role = getUserRole(email);
    return role === ROLES.DIRECTOR || role === ROLES.ADMIN;
  } catch {
    return false;
  }
}

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}

export default function OpenMatters() {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("all");
  const [sortField, setSortField] = useState("latest_activity");
  const [sortDir, setSortDir] = useState("desc");
  const [supportMap, setSupportMap] = useState({});
  const [aiResults, setAiResults] = useState([]);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const aiTimerRef = useRef(null);
  const navigate = useNavigate();
  const canRemove = useMemo(() => canRemoveMatters(), []);
  const [showRemoved, setShowRemoved] = useState(false);

  useEffect(() => {
    fetchSupportMap();
  }, []);

  useEffect(() => {
    fetchMatters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRemoved]);

  const fetchMatters = async () => {
    try {
      const res = await axios.get(`${API}/matters/list`, { params: showRemoved ? { removed: true } : {} });
      setMatters(res.data);
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportMap = async () => {
    try {
      const res = await axios.get(`${API}/lookup/fee-earner-support-map`);
      setSupportMap(res.data || {});
    } catch { /* non-critical */ }
  };

  // AI-powered search — debounced, fires after 800ms of typing
  const runAiSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setAiResults([]);
      setAiExplanation("");
      return;
    }
    setAiSearching(true);
    try {
      const res = await axios.post(`${API}/hub/ai-search`, { query, limit: 15 });
      // Only show SharePoint-only results (CaseMinder results already shown via local filter)
      const spOnly = (res.data.results || []).filter(r => r.source === "sharepoint");
      setAiResults(spOnly);
      setAiExplanation(res.data.explanation || "");
    } catch {
      setAiResults([]);
    } finally {
      setAiSearching(false);
    }
  }, []);

  useEffect(() => {
    if (aiTimerRef.current) clearTimeout(aiTimerRef.current);
    if (search.trim().length >= 2) {
      aiTimerRef.current = setTimeout(() => runAiSearch(search.trim()), 800);
    } else {
      setAiResults([]);
      setAiExplanation("");
    }
    return () => { if (aiTimerRef.current) clearTimeout(aiTimerRef.current); };
  }, [search, runAiSearch]);

  const feeEarners = useMemo(() => {
    const set = new Set(matters.map(m => m.fee_earner).filter(Boolean));
    return [...set].sort();
  }, [matters]);

  const filtered = useMemo(() => {
    // Only show Golden Rule–open matters in this view
    // The removed view (BM-H3468) lists every removed matter, open or not.
    let list = showRemoved ? [...matters] : matters.filter(m => m.golden_rule_open === true);
    if (feeEarnerFilter !== "all") {
      const allowed = new Set([feeEarnerFilter, ...(supportMap[feeEarnerFilter] || [])]);
      list = list.filter(m => allowed.has(m.fee_earner));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        (m.matter_ref || "").toLowerCase().includes(q) ||
        clientDisplayName(m).toLowerCase().includes(q) ||
        (m.matter_title || "").toLowerCase().includes(q) ||
        (m.fee_earner || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      if (sortField === "wip") {
        aVal = a.wip?.value || 0;
        bVal = b.wip?.value || 0;
      }
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return list;
  }, [matters, search, feeEarnerFilter, sortField, sortDir, supportMap, showRemoved]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const totalWip = useMemo(() => filtered.reduce((s, m) => s + (m.wip?.value || 0), 0), [filtered]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-hover/60 flex items-center justify-center">
        <div className="animate-pulse text-slate-400" data-testid="matters-loading">Loading matters...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-hover/60" data-testid="open-matters-page">
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="mb-2 -ml-2 text-xs text-slate-400"
          data-testid="matters-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Dashboard
        </Button>
        {/* 27 Oct 2026 system-wide refresh: header band only; the filters
            and table below keep their density. */}
        <PageHero
          icon={Briefcase}
          title={showRemoved ? "Removed Matters" : "Open Matters"}
          subtitle={`${filtered.length} matters`}
          tile="linear-gradient(135deg, #8b5cf6, #6366f1 55%, #3b82f6)"
          testId="open-matters-hero"
        >
          <div className="cm-glass rounded-full px-4 py-1.5 text-sm font-medium text-white ring-1 ring-white/20" data-testid="total-wip">
            Total WIP: {formatCurrency(totalWip)}
          </div>
        </PageHero>
        <PageScopeBanner
          text="Matters ready to work on — compliance is complete (CCL signed). Still onboarding a matter?"
          linkTo="/pipeline"
          linkLabel="Pipeline Tracker"
          testId="open-matters-scope-banner"
        />
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px] max-w-[350px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search matter, client, fee earner..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="matters-search"
            />
          </div>
          <select
            value={feeEarnerFilter}
            onChange={e => setFeeEarnerFilter(e.target.value)}
            className="h-9 rounded-md border border-navy-border text-sm px-3 bg-white"
            data-testid="matters-fee-earner-filter"
          >
            <option value="all">All Fee Earners</option>
            {feeEarners.map(fe => (
              <option key={fe} value={fe}>
                {fe}{supportMap[fe] ? ` (+ ${supportMap[fe].join(", ")})` : ""}
              </option>
            ))}
          </select>
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setLoading(true); setShowRemoved(v => !v); }}
              data-testid="matters-show-removed-toggle"
            >
              {showRemoved ? "Back to Open Matters" : "Show removed"}
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="glass-card shadow-sm overflow-x-auto" data-testid="matters-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-border bg-navy-hover/60/50">
                <th className="text-left px-4 py-3 font-semibold text-txt-muted cursor-pointer select-none" onClick={() => toggleSort("matter_ref")}>
                  <span className="flex items-center gap-1">Matter Ref <SortIcon field="matter_ref" /></span>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-txt-muted cursor-pointer select-none" onClick={() => toggleSort("client_name")}>
                  <span className="flex items-center gap-1">Client <SortIcon field="client_name" /></span>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-txt-muted cursor-pointer select-none" onClick={() => toggleSort("fee_earner")}>
                  <span className="flex items-center gap-1">Fee Earner <SortIcon field="fee_earner" /></span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-txt-muted cursor-pointer select-none" onClick={() => toggleSort("wip")}>
                  <span className="flex items-center gap-1 justify-end">WIP (90d) <SortIcon field="wip" /></span>
                </th>
                <th className="text-center px-4 py-3 font-semibold text-txt-muted">Last Invoice</th>
                <th className="text-center px-4 py-3 font-semibold text-txt-muted cursor-pointer select-none" onClick={() => toggleSort("latest_activity")}>
                  <span className="flex items-center gap-1 justify-center" title="Date of most recent CaseMinder form submission">Last Activity <SortIcon field="latest_activity" /></span>
                </th>
                {canRemove && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={canRemove ? 7 : 6} className="text-center py-12 text-slate-400" data-testid="matters-empty">
                    {search ? "No matching matters" : showRemoved ? "No removed matters" : "No matters found"}
                  </td>
                </tr>
              ) : (
                filtered.map(matter => (
                  <tr
                    key={matter.matter_ref}
                    className="border-b border-navy-border hover:bg-navy-hover/60/50 transition-colors cursor-pointer"
                    onClick={() => navigate(matterPath(matter.matter_ref))}
                    title={`Open ${matter.matter_ref}`}
                    data-testid={`matter-row-${matter.matter_ref}`}
                  >
                    {/* Matter Ref */}
                    <td className="px-4 py-3">
                      <Link
                        to={matterPath(matter.matter_ref)}
                        onClick={e => e.stopPropagation()}
                        className="font-mono font-semibold text-white hover:underline"
                        data-testid={`matter-link-${matter.matter_ref}`}
                      >
                        {matter.matter_ref}
                      </Link>
                      {matter.forms?.pre_caseminder && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-slate-500/40 text-txt-muted" title="Opened before CaseMinder">
                          Pre-CM
                        </span>
                      )}
                      {matter.matter_title && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[200px]">{matter.matter_title}</p>
                      )}
                      {matter.removed && (
                        <p className="text-xs text-status-amber mt-0.5 max-w-[240px]" data-testid={`removed-note-${matter.matter_ref}`}>
                          Removed by {matter.removed.removed_by_name || matter.removed.removed_by_email} on {formatDate(matter.removed.removed_at)}: {matter.removed.reason}
                        </p>
                      )}
                    </td>

                    {/* Client */}
                    <td className="px-4 py-3 text-txt max-w-[180px] truncate">{clientDisplayName(matter) || "—"}</td>

                    {/* Fee Earner */}
                    <td className="px-4 py-3 text-txt-muted">{matter.fee_earner || "—"}</td>

                    {/* WIP */}
                    <td className="px-4 py-3 text-right">
                      {matter.wip?.value > 0 ? (
                        <div>
                          <span className="font-semibold text-white">{formatCurrency(matter.wip.value)}</span>
                          <p className="text-xs text-slate-500">{matter.wip.hours}h</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Last Invoice */}
                    <td className="px-4 py-3 text-center">
                      {matter.last_invoice ? (
                        <div>
                          <p className="text-xs">{formatDate(matter.last_invoice.date)}</p>
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                            matter.last_invoice.status === "processed" || matter.last_invoice.status === "paid"
                              ? "bg-status-green-dim text-status-green"
                              : "bg-status-amber-dim/30 text-status-amber"
                          }`}>
                            {matter.last_invoice.status === "processed" || matter.last_invoice.status === "paid" ? (
                              <><CheckCircle2 className="w-3 h-3" /> Paid</>
                            ) : (
                              <><AlertCircle className="w-3 h-3" /> Awaiting Payment</>
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">No invoice</span>
                      )}
                    </td>

                    {/* Activity */}
                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {formatDate(matter.latest_activity)}
                    </td>

                    {/* BM-H3468: Remove / Restore */}
                    {canRemove && (
                      <td className="px-4 py-3 text-right cursor-default" onClick={e => e.stopPropagation()}>
                        <OpenMatterRemoveButton
                          matterRef={matter.matter_ref}
                          mode={showRemoved ? "restore" : "remove"}
                          onDone={fetchMatters}
                        />
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* AI Search: SharePoint results */}
        {search.trim().length >= 2 && (aiSearching || aiResults.length > 0) && (
          <div className="mt-4" data-testid="ai-sp-results-section">
            <div className="flex items-center gap-2 mb-2 text-xs">
              <Globe className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-txt-muted font-medium">SharePoint Document Library</span>
              {aiSearching && <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
              {aiExplanation && !aiSearching && <span className="text-txt-muted">— {aiExplanation}</span>}
            </div>
            {aiResults.length > 0 ? (
              <div className="glass-card shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-navy-border bg-navy-hover/60/50">
                      <th className="text-left px-4 py-2 font-semibold text-txt-muted text-xs">Matter Ref</th>
                      <th className="text-left px-4 py-2 font-semibold text-txt-muted text-xs">Title</th>
                      <th className="text-left px-4 py-2 font-semibold text-txt-muted text-xs">Source</th>
                      <th className="text-right px-4 py-2 font-semibold text-txt-muted text-xs"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiResults.map((r, i) => (
                      <tr
                        key={r.id || i}
                        className="border-b border-navy-border hover:bg-navy-hover/60/50 transition-colors cursor-pointer"
                        onClick={() => r.web_url && window.open(r.web_url, "_blank")}
                        data-testid={`sp-result-${i}`}
                      >
                        <td className="px-4 py-2.5">
                          <span className="font-mono font-semibold text-white">{r.secondary_label || r.matter_ref || "—"}</span>
                        </td>
                        <td className="px-4 py-2.5 text-txt">{clientDisplayName(r) || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-900/30 text-blue-400 border border-blue-500/30">
                            <Globe className="w-3 h-3" /> SharePoint
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {r.web_url && <ExternalLink className="w-3.5 h-3.5 text-blue-400 inline" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (!aiSearching && (
              <p className="text-xs text-txt-muted px-1">No additional matters found on SharePoint.</p>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
